import { z } from 'zod';
import { ActorRef, Id, Timestamp, cursorPage } from './common.js';
import { ANNOTATION_MAX_PER_COMMENT, Annotation, AnnotationInput } from './annotations.js';

export const COMMENT_BODY_MAX = 20000;

export interface Comment {
  id: string;
  workspace_id: string;
  asset_id: string;
  version_id: string;
  version_number: number;
  parent_comment_id: string | null;
  author: ActorRef;
  author_member_id?: string | null;
  guest_name?: string | null;
  body: string;
  body_html?: string;
  mentions?: string[];
  is_system?: boolean;
  timestamp_ms?: number | null;
  end_timestamp_ms?: number | null;
  frame_index?: number | null;
  fps_at_creation?: number | null;
  page_index?: number | null;
  resolved: boolean;
  resolved_by?: string | null;
  resolved_at?: string | null;
  annotations: Annotation[];
  replies?: Comment[];
  reply_count?: number;
  temp_id?: string | null;
  created_at: string;
  edited_at?: string | null;
  deleted_at?: string | null;
}

export const Comment: z.ZodType<Comment> = z.object({
  id: Id,
  workspace_id: Id,
  asset_id: Id,
  version_id: Id,
  version_number: z.int(),
  parent_comment_id: Id.nullable(),
  author: ActorRef,
  author_member_id: Id.nullable().optional(),
  guest_name: z.string().nullable().optional(),
  body: z.string().max(COMMENT_BODY_MAX),
  body_html: z.string().optional(),
  /** Release 3 REV-1: member ids resolved from `@[Name](member:<uuid>)` tokens (see release3.ts `MENTION_TOKEN_RE`). */
  mentions: z.array(Id).optional(),
  is_system: z.boolean().optional(),
  timestamp_ms: z.int().min(0).nullable().optional(),
  end_timestamp_ms: z.int().min(0).nullable().optional(),
  frame_index: z.int().min(0).nullable().optional(),
  fps_at_creation: z.number().nullable().optional(),
  page_index: z.int().min(0).nullable().optional(),
  resolved: z.boolean(),
  resolved_by: Id.nullable().optional(),
  resolved_at: Timestamp.nullable().optional(),
  annotations: z.array(Annotation),
  replies: z.array(z.lazy(() => Comment)).optional(),
  reply_count: z.int().optional(),
  temp_id: z.string().nullable().optional(),
  created_at: Timestamp,
  edited_at: Timestamp.nullable().optional(),
  deleted_at: Timestamp.nullable().optional(),
});

export const CommentPage = cursorPage(Comment).extend({
  unresolved_count: z.int().optional(),
});
export type CommentPage = z.infer<typeof CommentPage>;

export const CommentCreate = z.object({
  version_id: Id,
  body: z.string().min(1).max(COMMENT_BODY_MAX),
  timestamp_ms: z.int().min(0).nullable().optional(),
  end_timestamp_ms: z.int().min(0).nullable().optional(),
  frame_index: z.int().min(0).nullable().optional(),
  page_index: z.int().min(0).nullable().optional(),
  annotations: z.array(AnnotationInput).max(ANNOTATION_MAX_PER_COMMENT).optional(),
  temp_id: z.string().max(64).optional(),
});
export type CommentCreate = z.infer<typeof CommentCreate>;

export const CommentReplyCreate = z.object({
  body: z.string().min(1).max(COMMENT_BODY_MAX),
  temp_id: z.string().max(64).optional(),
});
export type CommentReplyCreate = z.infer<typeof CommentReplyCreate>;

export const CommentUpdate = z
  .object({
    body: z.string().min(1).max(COMMENT_BODY_MAX).optional(),
    timestamp_ms: z.int().min(0).nullable().optional(),
    end_timestamp_ms: z.int().min(0).nullable().optional(),
    page_index: z.int().min(0).nullable().optional(),
    annotations: z.array(AnnotationInput).max(ANNOTATION_MAX_PER_COMMENT).optional(),
  })
  .refine((o) => Object.values(o).some((v) => v !== undefined), {
    message: 'at least one field is required',
  });
export type CommentUpdate = z.infer<typeof CommentUpdate>;

export const GuestCommentCreate = CommentCreate.extend({
  guest_name: z.string().min(1).max(120),
  parent_comment_id: Id.nullable().optional(),
});
export type GuestCommentCreate = z.infer<typeof GuestCommentCreate>;

/** `frame_index = round(timestamp_ms * fps / 1000)` at creation (01 §2.11). */
export function frameIndexForTimestamp(timestampMs: number, fps: number): number {
  return Math.round((timestampMs * fps) / 1000);
}
